import React, { createContext, useContext, useReducer, useEffect, ReactNode, useRef, useCallback } from 'react';
import Cookies from 'js-cookie';
import { api } from '../services/api';

export interface UserInfo {
  email: string;
  name: string;
  user_id: string;
  picture?: string;
  given_name?: string;
  family_name?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: UserInfo | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

type AuthAction = 
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: UserInfo; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

interface AuthContextType extends AuthState {
  login: (googleToken: string) => Promise<void>;
  loginWithCode: (code: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  getGoogleAuthUrl: () => Promise<string>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const logout = () => {
    Cookies.remove('auth_token');
    dispatch({ type: 'LOGOUT' });
  };

  // Auto-logout after 5 minutes of inactivity
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    
    if (state.isAuthenticated) {
      console.log('Starting inactivity timer - 5 minutes');
      inactivityTimeoutRef.current = setTimeout(() => {
        console.log('Auto-logout due to inactivity - 5 minutes elapsed');
        logout();
      }, 5 * 60 * 1000); // 5 minutes
    }
  }, [state.isAuthenticated]);

  // Track user activity
  useEffect(() => {
    if (state.isAuthenticated) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      const handleActivity = () => {
        console.log('User activity detected - resetting inactivity timer');
        resetInactivityTimer();
      };

      events.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });

      // Start the timer
      resetInactivityTimer();

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleActivity, true);
        });
        if (inactivityTimeoutRef.current) {
          clearTimeout(inactivityTimeoutRef.current);
        }
      };
    }
  }, [state.isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for existing token on mount
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = Cookies.get('auth_token');
      if (token) {
        try {
          // Validate token with backend
          const response = await api.get('/user/profile', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user: response.data,
              token,
            },
          });
        } catch (error) {
          // Token is invalid, remove it
          Cookies.remove('auth_token');
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'LOGOUT' });
      }
    };

    checkExistingAuth();
  }, []);

  const login = async (googleToken: string) => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const formData = new FormData();
      formData.append('google_token', googleToken);

      const response = await api.post('/auth/google', formData);
      const { access_token, user_info } = response.data;

      // Store token in cookie
      Cookies.set('auth_token', access_token, { 
        expires: 1, // 1 day
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: user_info,
          token: access_token,
        },
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Authentication failed';
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage,
      });
      throw error;
    }
  };

  const loginWithCode = async (code: string) => {
    console.log('🚀 loginWithCode called');
    console.log(`🚀 Code received in AuthContext: ${code.substring(0, 20)}...`);
    dispatch({ type: 'AUTH_START' });
    
    try {
      console.log('🔵 Creating FormData with code');
      const formData = new FormData();
      formData.append('code', code);
      console.log('🔵 FormData created successfully');

      console.log('🔵 Sending POST request to /auth/google/callback');
      console.log(`🔵 API base URL: ${api.defaults.baseURL}`);
      const response = await api.post('/auth/google/callback', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('✅ Response received from backend');
      console.log('✅ Response status:', response.status);
      console.log('✅ Response data keys:', Object.keys(response.data));
      const { access_token, user_info } = response.data;
      console.log('✅ Extracted access_token and user_info');
      console.log(`✅ Access token length: ${access_token?.length}`);
      console.log('✅ User email:', user_info?.email);

      // Store token in cookie
      console.log('🍪 Storing token in cookie');
      Cookies.set('auth_token', access_token, { 
        expires: 1, // 1 day
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
      console.log('🍪 Token stored in cookie');

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: user_info,
          token: access_token,
        },
      });
      console.log('✅ Auth success dispatched');
    } catch (error: any) {
      console.error('❌ Error in loginWithCode:', error);
      console.error('❌ Error response:', error?.response);
      console.error('❌ Error data:', error?.response?.data);
      console.error('❌ Error message:', error?.message);
      const errorMessage = error.response?.data?.detail || 'Authentication failed';
      console.error('❌ Error message to display:', errorMessage);
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage,
      });
      throw error;
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const getGoogleAuthUrl = async (): Promise<string> => {
    try {
      const response = await api.get('/auth/google/url');
      return response.data.auth_url;
    } catch (error) {
      throw new Error('Failed to get Google auth URL');
    }
  };

  const checkAuth = async () => {
    const token = Cookies.get('auth_token');
    if (token) {
      try {
        // Validate token with backend
        const response = await api.get('/user/profile', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: response.data,
            token,
          },
        });
      } catch (error) {
        // Token is invalid, remove it
        Cookies.remove('auth_token');
        dispatch({ type: 'LOGOUT' });
      }
    } else {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginWithCode,
    logout,
    clearError,
    getGoogleAuthUrl,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}


